import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  startCaptureJob,
  completeCaptureJob,
  removeJob,
  type MixSettings,
} from '../lib/api.js'
import { resumableStorageUpload } from '../lib/storageUpload.js'
import { cn } from '../lib/cn.js'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface CaptureInfo {
  jobId:        string
  videoId:      string
  videoUrl:     string
  targetLang:   string
  stream:       MediaStream
  uploadUrl:    string
  mixSettings?: MixSettings
}

// ── Mobile file upload modal ──────────────────────────────────────────────────

type Phase = 'file_idle' | 'file_uploading' | 'done' | 'error'

export interface CaptureModalProps {
  videoUrl:    string
  targetLang:  string
  sourceLang:  string
  mixSettings: MixSettings
  onClose:     ()                                               => void
  onJobCreated: (jobId: string, videoUrl: string, lang: string) => void
}

export default function CaptureModal({ videoUrl, targetLang, sourceLang, mixSettings, onClose, onJobCreated }: CaptureModalProps) {
  const { t } = useTranslation()

  const [phase,          setPhase]          = useState<Phase>('file_idle')
  const [selectedFile,   setSelectedFile]   = useState<File | null>(null)
  const [errorMsg,       setErrorMsg]       = useState('')
  const [uploadPercent,  setUploadPercent]  = useState(0)

  const captureJobIdRef = useRef('')

  useEffect(() => {
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      if (phase === 'file_idle' || phase === 'done' || phase === 'error') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [phase, onClose])

  const handleFileUpload = async () => {
    if (!selectedFile) return
    setPhase('file_uploading')
    setUploadPercent(0)
    try {
      const job = await startCaptureJob({
        targetLang,
        captureRate: 1,
        videoUrl,
        sourceLang,
        mixSettings,
      })
      captureJobIdRef.current = job.id

      const { promise } = resumableStorageUpload(
        job.storagePath,
        selectedFile,
        selectedFile.type || 'audio/webm',
        (p) => setUploadPercent(p.percent),
      )
      await promise
      await completeCaptureJob(job.id)
      setPhase('done')
      onJobCreated(job.id, videoUrl, targetLang)
    } catch (err) {
      if (captureJobIdRef.current) void removeJob(captureJobIdRef.current)
      setErrorMsg((err as Error).message)
      setPhase('error')
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={e => { if (e.target === e.currentTarget && phase !== 'file_uploading') onClose() }}
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/55 backdrop-blur-sm"
    >
      <div className="card w-full max-w-[480px] max-h-[calc(100dvh-2rem)] overflow-y-auto p-7 relative shadow-glass">

        {phase !== 'file_uploading' && (
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="absolute top-4 right-4 bg-transparent border-none text-text-muted text-xl leading-none cursor-pointer py-0.5 px-1.5"
          >×</button>
        )}

        <h2 className="text-xl font-bold mb-6 pr-8">
          {t('capture.uploadBtn')}
        </h2>

        {/* File picker */}
        {phase === 'file_idle' && (
          <div className="flex flex-col gap-4">
            <p className="text-[0.9rem] text-text-muted leading-normal mb-1">
              {t('capture.mobileHint')}
            </p>

            <label
              htmlFor="capture-file-input"
              className={cn(
                'flex flex-col items-center justify-center gap-2 p-8 px-4',
                'border-2 border-dashed border-glass-border rounded-md cursor-pointer text-center transition-colors duration-200',
                selectedFile && 'bg-surface-raised',
              )}
            >
              <span className="text-3xl">🎵</span>
              <span className={cn('text-[0.9rem]', selectedFile ? 'font-semibold text-text' : 'text-text-muted')}>
                {selectedFile ? selectedFile.name : t('capture.uploadLabel')}
              </span>
              {!selectedFile && (
                <span className="text-[0.78rem] text-text-muted">{t('capture.uploadHint')}</span>
              )}
              <input
                id="capture-file-input"
                type="file"
                accept="audio/*,video/*"
                className="hidden"
                onChange={e => setSelectedFile(e.target.files?.[0] ?? null)}
              />
            </label>

            <button
              type="button"
              disabled={!selectedFile}
              onClick={() => { void handleFileUpload() }}
              className={cn(
                'btn-primary w-full',
                !selectedFile && 'bg-surface-raised text-text-muted cursor-not-allowed',
              )}
            >
              {t('capture.uploadBtn')}
            </button>
          </div>
        )}

        {/* Uploading */}
        {phase === 'file_uploading' && (
          <div className="text-center py-8">
            <p className="font-semibold mb-4">
              {t('dashboard.uploadProgress', { percent: uploadPercent })}
            </p>
            <div className="progress-track progress-track-lg">
              <div
                className="progress-fill-solid"
                style={{ width: `${uploadPercent}%` }}
              />
            </div>
            <p className="text-[0.85rem] text-text-muted mt-3">{t('capture.uploadingHint')}</p>
          </div>
        )}

        {/* Done */}
        {phase === 'done' && (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center mx-auto mb-5 text-[1.75rem]">
              ✓
            </div>
            <p className="font-bold text-lg mb-2">{t('capture.doneTitle')}</p>
            <p className="text-[0.9rem] text-text-muted leading-normal mb-6">{t('capture.doneDesc')}</p>
            <button type="button" onClick={onClose} className="btn-primary w-full">{t('capture.closeDone')}</button>
          </div>
        )}

        {/* Error */}
        {phase === 'error' && (
          <div className="text-center py-6">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-5 text-[1.75rem]">
              ✕
            </div>
            <p className="font-bold text-lg mb-2">{t('capture.errorTitle')}</p>
            {errorMsg && (
              <p className="text-[0.83rem] text-error mb-4 break-words">{errorMsg}</p>
            )}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => { setPhase('file_idle'); setErrorMsg('') }}
                className="btn-primary flex-1"
              >
                {t('capture.retryBtn')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="btn-ghost flex-1"
              >
                {t('common.close')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
